import React, { useState, useMemo, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Paper,
  Avatar,
  Typography,
  Theme,
  Toolbar,
  Divider,
  CircularProgress,
  alpha,
} from '@mui/material';
import GVIconButton from 'components/lib/GVIconButton/GVIconButton';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { ListCheck } from '@globalvisioninc/assets/icons';
import { getStyleVariables } from 'styles/vars';
import { useQuery } from '@redux-requests/react';
import { User } from '@auth0/auth0-react';
import EditTeamMember from 'pages/MyAccount/myTeam/EditTeamMember';
import { GVUserStatus } from 'components';
import { downloadMyTeam } from 'store/request/users/actions';
import { useDispatch } from 'react-redux';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import { GVTypography } from 'components/lib';
import { isSuperAdmin } from 'utils/auth';
import { MemberDetails } from 'store/myAccount/types';
import { fetchActiveUserOrganization } from 'store/myAccount/requests';
import { USER_ROLES } from './constants';
import { makeStyles } from 'tss-react/mui';
import { useUserStateStore } from 'zstore/userStateStore';
import { SearchBar } from '@globalvisioninc/ui/compositions';
import styles from './YourTeam.module.css';

const useStyles = makeStyles()((theme: Theme) => {
  const styleVariables = getStyleVariables(theme);
  return {
    disabledUser: {
      '& .MuiTableCell-root': {
        color: alpha(theme.palette.common.white, 0.25),
      },
      '& .MuiAvatar-img': { opacity: 0.3 },
    },
    scrollBar: { ...styleVariables.scrollBar, scrollbarGutter: 'auto' },
  };
});

export default function EnhancedTable() {
  const { classes } = useStyles();
  const { user } = useUserStateStore();
  const dispatch = useDispatch();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterValue, setFilterValue] = useState('');
  const authId = user?.sub;
  const superAdmin = isSuperAdmin(user as User);
  const { loading: loadingCsv } = useQuery(downloadMyTeam());
  const { data: organization } = useQuery({ type: fetchActiveUserOrganization });
  const ssoEnabled = organization?.ssoEnabled;
  const scimManaged = organization?.scimManaged === true;
  const members = organization?.members || [];
  const userIdentifierKey = ssoEnabled ? 'email' : 'name';
  const capitalizedUserIdentifierKey = userIdentifierKey.charAt(0).toUpperCase() + userIdentifierKey.slice(1);

  const getUserIdentifier = (member: MemberDetails): string => {
    let identifier = member.name;
    if (ssoEnabled) {
      identifier = member.email;
    }
    if (!identifier) return '';
    return identifier;
  };

  const filteredUsers = useMemo(() => {
    if (filterValue === '') {
      return members;
    }

    const lowercaseFilterValue = filterValue.trim().toLowerCase();
    const filterValues = lowercaseFilterValue.split(' ');

    return members.filter((member) => {
      const name = getUserIdentifier(member).toLowerCase() || '';
      const role = member.role?.toLowerCase() || '';
      const status = (member.status === 'expired' ? 'invite expired' : member.status).toLowerCase() || '';
      // each keyword in the search value must match an attribute of the filtered user
      return filterValues.every(
        (searchWord) => name.includes(searchWord) || role.includes(searchWord) || status.includes(searchWord),
      );
    });
  }, [members, filterValue]);

  useEffect(() => {
    if (page) {
      const lastPossiblePageIndex = Math.ceil(filteredUsers.length / rowsPerPage) - 1;
      if (lastPossiblePageIndex < 0) {
        setPage(0);
      } else if (lastPossiblePageIndex < page) {
        setPage(lastPossiblePageIndex);
      }
    }
  }, [filteredUsers, page, rowsPerPage]);

  const headCells = [
    { id: userIdentifierKey, alignRight: false, label: capitalizedUserIdentifierKey, minWidth: 250 },
    { id: 'role', alignRight: false, label: 'Role', minWidth: 200 },
    { id: 'date', alignRight: false, label: 'Date joined', minWidth: 200 },
    { id: 'login', alignRight: false, label: 'Last Login', minWidth: 200 },
    { id: 'status', alignRight: false, label: 'Status', minWidth: 155 },
    { id: 'actions', alignRight: true, label: 'Actions', minWidth: 93 },
  ];

  function EnhancedTableHead() {
    return (
      <TableHead>
        <TableRow hover>
          {headCells.map((headCell) => (
            <TableCell
              key={headCell.id}
              align={headCell.alignRight ? 'right' : 'left'}
              style={{ minWidth: headCell.minWidth }}
            >
              {headCell.label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
    );
  }

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  return (
    <Paper className={styles.paper}>
      {/* show overlay while loading csv to disable UI */}
      {loadingCsv && (
        <div
          style={{
            position: 'absolute',
            zIndex: 1100,
            width: '100%',
            height: 'calc(100% - 64px)',
            background: 'rgba(41, 43, 48, 0.62)',
            marginTop: '64px',
          }}
        />
      )}
      <TableContainer className={`${styles.table} ${classes.scrollBar}`}>
        <Toolbar disableGutters className={styles.toolbar}>
          <SearchBar
            value={filterValue}
            onChange={setFilterValue}
            placeholder="Search by email, role or status"
            isDisabled={loadingCsv}
          />
          {loadingCsv ? (
            <GVTooltip title={<GVTypography variant="inherit">Generating File...</GVTypography>}>
              <CircularProgress color="secondary" className={styles.loading} />
            </GVTooltip>
          ) : (
            <GVTooltip title={<GVTypography variant="inherit">Export to .CSV</GVTypography>}>
              <GVIconButton
                testId="my-team-download-csv"
                icon={<SaveAltIcon />}
                onClick={() => {
                  dispatch(downloadMyTeam());
                }}
                color="primary"
              />
            </GVTooltip>
          )}
        </Toolbar>
        <Divider light />
        <Table aria-labelledby="tableTitle" size="small" aria-label="enhanced table" stickyHeader id="myteam-table">
          <EnhancedTableHead />
          <TableBody>
            {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => {
              const userIdentifier = getUserIdentifier(row);
              const labelId = `enhanced-table-checkbox-${index}`;
              const dateString = new Intl.DateTimeFormat('en-GB').format(new Date(row.createdAt));
              const lastLoginString = row.lastLogin
                ? new Intl.DateTimeFormat('en-GB').format(new Date(row.lastLogin))
                : 'N/A';
              const isScimManagedUser = scimManaged && row.scimManaged === true;
              return (
                <TableRow
                  tabIndex={-1}
                  key={labelId}
                  className={
                    row.id === authId || (row.role === USER_ROLES.TENANT_ADMIN && !superAdmin)
                      ? classes.disabledUser
                      : undefined
                  }
                >
                  <TableCell component="th" id={labelId} scope="row">
                    <div className={styles.member}>
                      <Avatar alt={userIdentifier} className={styles.avatar} src={row.picture}>
                        {!row.picture && userIdentifier.charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography variant="body1">{userIdentifier}</Typography>
                    </div>
                  </TableCell>
                  <TableCell align="left">
                    <div className={styles.role}>
                      {row.role}
                      {row.hasAuditTrail && (
                        <GVTooltip title="Audit trail access">
                          <span className={styles.audit} role="img" aria-label="Audit trail access">
                            <ListCheck />
                          </span>
                        </GVTooltip>
                      )}
                    </div>
                  </TableCell>
                  <TableCell align="left">{dateString}</TableCell>
                  <TableCell align="left">{lastLoginString}</TableCell>
                  <TableCell align="left">
                    <div className={styles.status}>
                      <GVUserStatus status={row.status} />
                    </div>
                  </TableCell>
                  <TableCell align="right">
                    <EditTeamMember
                      userId={row.id}
                      inviteId={row.inviteId}
                      isWhitelisted={row.isWhitelisted}
                      index={index}
                      userInfo={{
                        firstName: row.firstName,
                        lastName: row.lastName,
                        role: row.role as string,
                        email: row.email as string,
                      }}
                      disabled={row.id === authId || (row.role === USER_ROLES.TENANT_ADMIN && !superAdmin)}
                      permissionOnly={isScimManagedUser}
                      status={row.status}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        className={styles.pagination}
        rowsPerPageOptions={[10, 15, 25, 50]}
        component="div"
        count={filteredUsers.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </Paper>
  );
}
